import React, { useState, useEffect, useCallback, useRef } from "react";
import { schoolIndentAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import {
  Users, CalendarDays, Loader, Search, BarChart3, Check,
  Coffee, Sun, ChevronDown, ChevronRight, X, Keyboard, Plus, Trash2
} from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import axios from "axios";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CLASS_LIST  = ["PP1","PP2","Class 1","Class 2","Class 3","Class 4","Class 5",
                     "Class 6","Class 7","Class 8","Class 9","Class 10","Class 11","Class 12"];
const CLASS_SHORT = ["PP1","PP2","C1","C2","C3","C4","C5","C6","C7","C8","C9","C10","C11","C12"];

function totalOf(strength) {
  return Object.values(strength || {}).reduce((s, v) => s + (parseInt(v) || 0), 0);
}
function emptyStrength() {
  return Object.fromEntries(CLASS_LIST.map(c => [c, 0]));
}

const TABS = [
  { id: "entry",   label: "Strength Entry",   icon: Users },
  { id: "summary", label: "Daily Summary",    icon: BarChart3 },
  { id: "food",    label: "Food Requirement", icon: CalendarDays },
];

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

// ─── SCHOOL SEARCH HOOK ───────────────────────────────────────────────────────
function useSchoolSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/logistics/school-master`, {
          ...auth(), params: { search: query, limit: 12 }
        });
        setResults(r.data?.records || []);
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [query]);
  return { results, loading };
}

// ─── KEYBOARD-FIRST QUICK ENTRY ROW ──────────────────────────────────────────
function QuickEntryRow({ date, kitchenId, onSaved, todayEntries }) {
  const [schoolQuery, setSchoolQuery] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [mdmCount, setMdmCount] = useState("");       // Primary 1-5
  const [upperCount, setUpperCount] = useState("");   // Upper Primary 6-10 total
  const [bfCount, setBfCount] = useState("");
  const [eggCount, setEggCount] = useState("");       // Eggs served today (optional)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lastSaved, setLastSaved] = useState(null);

  const searchRef = useRef();
  const mdmRef = useRef();
  const upperRef = useRef();
  const bfRef = useRef();
  const eggRef = useRef();
  const dropRef = useRef();

  const { results, loading: searching } = useSchoolSearch(schoolQuery);

  // Close on outside click
  useEffect(() => {
    const fn = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropdownOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // Focus school search on mount
  useEffect(() => { searchRef.current?.focus(); }, []);

  const selectSchool = (school) => {
    setSelectedSchool(school);
    setSchoolQuery(school.school_name);
    setDropdownOpen(false);
    setTimeout(() => mdmRef.current?.focus(), 50);
  };

  const clearSchool = () => {
    setSelectedSchool(null);
    setSchoolQuery("");
    setDropdownOpen(false);
    setMdmCount("");
    setUpperCount("");
    setBfCount("");
    setEggCount("");
    setError("");
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDropdownOpen(true);
      setHighlightIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && dropdownOpen && results[highlightIdx]) {
      e.preventDefault();
      selectSchool(results[highlightIdx]);
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    } else if (e.key === "Tab" && selectedSchool) {
      // Tab from search to MDM when school already selected
    }
  };

  const handleBfKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  const handleSave = async () => {
    if (!selectedSchool) { setError("Select a school first"); searchRef.current?.focus(); return; }
    const primaryVal = parseInt(mdmCount) || 0;
    const upperVal   = parseInt(upperCount) || 0;
    const bfVal      = parseInt(bfCount) || 0;
    if (!primaryVal && !upperVal && !bfVal) { setError("Enter Primary, Upper Primary or Breakfast count"); mdmRef.current?.focus(); return; }
    setSaving(true); setError("");
    try {
      const entries = [];
      // MDM — band-based entry (primary 1-5 + upper primary 6-10)
      if (primaryVal > 0 || upperVal > 0) {
        entries.push({
          school_name: selectedSchool.school_name,
          route_id: null,
          route_number: selectedSchool.route_number || "",
          mandal: selectedSchool.mandal || "",
          date,
          kitchen_id: kitchenId || null,
          strength: { primary: primaryVal, upper_primary: upperVal },
          program_type: "MDM",
        });
      }
      if (bfVal > 0) {
        entries.push({
          school_name: selectedSchool.school_name,
          route_id: null,
          route_number: selectedSchool.route_number || "",
          mandal: selectedSchool.mandal || "",
          date,
          kitchen_id: kitchenId || null,
          strength: { Total: bfVal },
          program_type: "breakfast",
        });
      }
      // Egg supply — only when explicitly entered
      if (parseInt(eggCount) > 0) {
        entries.push({
          school_name: selectedSchool.school_name,
          route_id: null,
          route_number: selectedSchool.route_number || "",
          mandal: selectedSchool.mandal || "",
          date,
          kitchen_id: kitchenId || null,
          strength: { Total: parseInt(eggCount) },
          program_type: "egg",
        });
      }
      await schoolIndentAPI.bulkEntry({ date, kitchen_id: kitchenId || null, entries });
      setLastSaved(`${selectedSchool.school_name} — Primary: ${primaryVal}${upperVal ? `, Upper: ${upperVal} (6-8: ${Math.round(upperVal*0.65)}, 9-10: ${Math.round(upperVal*0.35)})` : ""}${bfVal ? `, BF: ${bfVal}` : ""}`);
      onSaved();
      clearSchool();
    } catch (e) { setError(formatError(e)); }
    setSaving(false);
  };

  return (
    <div className="bg-white border-2 border-[#E8956D]/30 rounded-2xl p-5 space-y-4">
      {/* Keyboard hint banner */}
      <div className="flex items-center gap-2 text-xs text-[#8A7E73] bg-[#F4F3F0] rounded-lg px-3 py-2">
        <Keyboard size={12} className="text-[#E8956D]" />
        <span><kbd className="bg-white border border-[#E5E1DB] rounded px-1 py-0.5 font-mono text-[10px]">↑↓</kbd> Navigate schools · <kbd className="bg-white border border-[#E5E1DB] rounded px-1 py-0.5 font-mono text-[10px]">Enter</kbd> Select / Save · <kbd className="bg-white border border-[#E5E1DB] rounded px-1 py-0.5 font-mono text-[10px]">Tab</kbd> Next field · <kbd className="bg-white border border-[#E5E1DB] rounded px-1 py-0.5 font-mono text-[10px]">Esc</kbd> Close</span>
      </div>

      {/* Main entry row */}
      <div className="flex gap-3 items-start flex-wrap lg:flex-nowrap">
        {/* School search */}
        <div className="flex-1 min-w-64 relative" ref={dropRef}>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name *</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73] z-10" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Type school name to search all 955+ schools…"
              value={schoolQuery}
              onChange={e => { setSchoolQuery(e.target.value); setSelectedSchool(null); setDropdownOpen(true); setHighlightIdx(0); }}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
              className={`w-full border rounded-lg pl-8 pr-8 py-2.5 text-sm outline-none transition-colors ${selectedSchool ? "border-[#637C64] bg-[#EDF2EE]" : "border-[#E5E1DB] focus:border-[#E8956D]"}`}
              autoComplete="off"
              data-testid="school-search-input"
            />
            {(schoolQuery || selectedSchool) && (
              <button onClick={clearSchool} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A7E73] hover:text-[#C2624E]">
                <X size={14} />
              </button>
            )}
            {searching && <Loader size={12} className="absolute right-8 top-1/2 -translate-y-1/2 animate-spin text-[#E8956D]" />}
          </div>

          {/* School info after selection */}
          {selectedSchool && (
            <p className="text-[10px] text-[#637C64] mt-1 font-medium">
              ✓ Route {selectedSchool.route_number} · {selectedSchool.mandal}
            </p>
          )}

          {/* Dropdown results */}
          {dropdownOpen && results.length > 0 && !selectedSchool && (
            <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-[#E5E1DB] rounded-xl shadow-xl max-h-64 overflow-y-auto">
              {results.map((s, i) => (
                <button
                  key={`${s.route_number}-${s.school_name}`}
                  onMouseDown={e => { e.preventDefault(); selectSchool(s); }}
                  className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${i === highlightIdx ? "bg-[#E8956D]/10 border-l-2 border-[#E8956D]" : "hover:bg-[#FAFAF8]"}`}
                  data-testid={`school-result-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#3A322C] truncate">{s.school_name}</p>
                    <p className="text-xs text-[#8A7E73]">{s.mandal} · Route {s.route_number}</p>
                  </div>
                  <span className="text-[10px] bg-[#F4F3F0] text-[#8A7E73] px-2 py-0.5 rounded-full shrink-0 mt-0.5">
                    {s.coordinator_name}
                  </span>
                </button>
              ))}
              {schoolQuery.length >= 2 && results.length === 0 && !searching && (
                <div className="px-4 py-3 text-sm text-[#8A7E73]">No schools found for "{schoolQuery}"</div>
              )}
            </div>
          )}
        </div>

        {/* Primary (1-5) count */}
        <div className="w-32 shrink-0">
          <label className="text-xs font-bold uppercase text-green-700 mb-1 block flex items-center gap-1">
            <Sun size={11} /> Primary (1-5)
          </label>
          <input
            ref={mdmRef}
            type="number" min="0" max="9999"
            value={mdmCount}
            onChange={e => setMdmCount(e.target.value)}
            onKeyDown={e => e.key === "Enter" && upperRef.current?.focus()}
            placeholder="0"
            className="w-full border border-green-200 rounded-lg px-3 py-2.5 text-sm text-center outline-none focus:border-green-500 bg-green-50 font-bold text-green-800"
            data-testid="mdm-count-input"
          />
          <p className="text-[10px] text-green-600 mt-1 text-center">₹6.78/child</p>
        </div>

        {/* Upper Primary (6-10) count — system auto-splits 65/35 */}
        <div className="w-40 shrink-0">
          <label className="text-xs font-bold uppercase text-blue-700 mb-1 block flex items-center gap-1">
            <Sun size={11} /> Upper Primary (6-10)
          </label>
          <input
            ref={upperRef}
            type="number" min="0" max="9999"
            value={upperCount}
            onChange={e => setUpperCount(e.target.value)}
            onKeyDown={e => e.key === "Enter" && bfRef.current?.focus()}
            placeholder="0"
            className="w-full border border-blue-200 rounded-lg px-3 py-2.5 text-sm text-center outline-none focus:border-blue-500 bg-blue-50 font-bold text-blue-800"
          />
          {upperCount > 0 && (
            <p className="text-[10px] text-blue-600 mt-1 text-center">
              6-8: {Math.round(upperCount*0.65)} · 9-10: {Math.round(upperCount*0.35)}
            </p>
          )}
          {!upperCount && <p className="text-[10px] text-blue-400 mt-1 text-center">65% → 6-8 · 35% → 9-10</p>}
        </div>

        {/* Breakfast count */}
        <div className="w-32 shrink-0">
          <label className="text-xs font-bold uppercase text-amber-700 mb-1 block flex items-center gap-1">
            <Coffee size={11} /> Breakfast
          </label>
          <input
            ref={bfRef}
            type="number" min="0" max="9999"
            value={bfCount}
            onChange={e => setBfCount(e.target.value)}
            onKeyDown={e => e.key === "Enter" && eggRef.current?.focus()}
            placeholder="0"
            className="w-full border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-center outline-none focus:border-amber-500 bg-amber-50 font-bold text-amber-800"
            data-testid="bf-count-input"
          />
        </div>

        {/* Egg count — only enter if eggs were supplied today */}
        <div className="w-32 shrink-0">
          <label className="text-xs font-bold uppercase text-purple-700 mb-1 block flex items-center gap-1">
            🥚 Eggs (optional)
          </label>
          <input
            ref={eggRef}
            type="number" min="0" max="9999"
            value={eggCount}
            onChange={e => setEggCount(e.target.value)}
            onKeyDown={handleBfKeyDown}
            placeholder="0"
            className="w-full border border-purple-200 rounded-lg px-3 py-2.5 text-sm text-center outline-none focus:border-purple-500 bg-purple-50 font-bold text-purple-800"
            data-testid="egg-count-input"
          />
          <p className="text-[10px] text-[#8A7E73] mt-1 text-center">Enter → Save</p>
        </div>

        {/* Save button */}
        <div className="flex items-end pb-0.5 shrink-0 mt-5">
          <button
            onClick={handleSave}
            disabled={saving || !selectedSchool}
            className="flex items-center gap-2 bg-[#637C64] text-white rounded-xl px-5 py-2.5 font-bold text-sm hover:bg-[#4e6350] disabled:opacity-50 transition-colors"
            data-testid="save-entry-btn"
          >
            {saving ? <Loader size={15} className="animate-spin" /> : <Plus size={15} />}
            Add Entry
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-[#FBEAE7] text-[#C2624E] rounded-lg px-3 py-2 text-sm">{error}</div>
      )}

      {lastSaved && (
        <div className="bg-[#EDF2EE] text-[#637C64] rounded-lg px-3 py-2 text-sm flex items-center gap-2">
          <Check size={14} /> Saved: <strong>{lastSaved}</strong>
        </div>
      )}
    </div>
  );
}

// ─── TODAY'S ENTRIES LOG ─────────────────────────────────────────────────────
function TodaysEntriesLog({ date, kitchenId, refreshKey }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await schoolIndentAPI.getEntries({ date, kitchen_id: kitchenId || null, limit: 500 });
      setEntries(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  }, [date, kitchenId, refreshKey]);

  useEffect(() => { fetch(); }, [fetch]);

  // Group by school name
  const grouped = entries.reduce((acc, e) => {
    const key = e.school_name;
    if (!acc[key]) acc[key] = { mdm: 0, breakfast: 0, primary: 0, upper: 0, route: e.route_number || "", mandal: e.mandal || "" };
    if (e.program_type === "breakfast") {
      acc[key].breakfast += e.total_students || 0;
    } else {
      // Support new band format
      const str = e.strength || {};
      if (str.primary != null || str.upper_primary != null) {
        acc[key].primary += (str.primary || 0);
        acc[key].upper   += (str.upper_primary || 0);
        acc[key].mdm     += (str.primary || 0) + (str.upper_primary || 0);
      } else {
        acc[key].mdm += e.total_students || 0;
      }
    }
    return acc;
  }, {});

  const filtered = Object.entries(grouped).filter(([name]) =>
    !search || name.toLowerCase().includes(search.toLowerCase())
  );

  const totals = filtered.reduce((acc, [, d]) => {
    acc.mdm += d.mdm; acc.breakfast += d.breakfast;
    acc.primary += d.primary; acc.upper += d.upper;
    return acc;
  }, { mdm: 0, breakfast: 0, primary: 0, upper: 0 });

  if (Object.keys(grouped).length === 0 && !loading) return (
    <div className="text-center py-8 text-[#8A7E73] bg-white border border-[#E5E1DB] rounded-xl">
      <Users size={32} className="mx-auto mb-2 opacity-20" />
      <p className="text-sm">No entries yet for {date}. Use the form above to add schools.</p>
    </div>
  );

  return (
    <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-[#F4F3F0] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <h3 className="font-bold text-[#3A322C] text-sm">Today's Entries — {date}</h3>
          {loading && <Loader size={12} className="animate-spin text-[#E8956D]" />}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {totals.primary > 0 && <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Primary 1-5: {totals.primary.toLocaleString()}</span>}
          {totals.upper > 0 && <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Upper 6-10: {totals.upper.toLocaleString()} (6-8: {Math.round(totals.upper*0.65)} · 9-10: {Math.round(totals.upper*0.35)})</span>}
          {totals.breakfast > 0 && <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">BF: {totals.breakfast.toLocaleString()}</span>}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
            <input placeholder="Filter schools…" value={search} onChange={e => setSearch(e.target.value)}
              className="border border-[#E5E1DB] rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:border-[#E8956D] w-40" />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F3F0] sticky top-0">
            <tr>
              {["#", "School Name", "Mandal", "Route", "Primary (1-5)", "Upper (6-10)", "6-8 (65%)", "9-10 (35%)", "Breakfast", "Total MDM"].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-[#5C5147] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F4F3F0]">
            {filtered.map(([name, d], i) => (
              <tr key={name} className="hover:bg-[#FAFAF8]">
                <td className="px-4 py-2.5 text-xs text-[#C4BAAF]">{i + 1}</td>
                <td className="px-4 py-2.5 font-medium text-[#3A322C]">{name}</td>
                <td className="px-4 py-2.5 text-xs text-[#8A7E73]">{d.mandal || "—"}</td>
                <td className="px-4 py-2.5">
                  {d.route && <span className="text-xs bg-[#E8956D]/10 text-[#E8956D] font-bold px-2 py-0.5 rounded-full">{d.route}</span>}
                </td>
                <td className="px-4 py-2.5 font-bold text-green-700">{d.primary > 0 ? d.primary.toLocaleString() : (d.mdm > 0 ? d.mdm.toLocaleString() : "—")}</td>
                <td className="px-4 py-2.5 font-bold text-blue-700">{d.upper > 0 ? d.upper.toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 text-blue-600">{d.upper > 0 ? Math.round(d.upper * 0.65).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 text-blue-500">{d.upper > 0 ? Math.round(d.upper * 0.35).toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 font-bold text-amber-700">{d.breakfast > 0 ? d.breakfast.toLocaleString() : "—"}</td>
                <td className="px-4 py-2.5 font-extrabold text-[#3A322C]">{(d.mdm + d.breakfast).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function SchoolIndent() {
  const { selectedKitchen } = useKitchen();
  const [activeTab, setActiveTab] = useState("entry");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [foodReq, setFoodReq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const dateRef = useRef();

  // Also keep route-based mode for bulk entry
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  const [bulkData, setBulkData] = useState({});
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [entryMode, setEntryMode] = useState("quick"); // "quick" | "route"

  useEffect(() => {
    logisticsAPI.getRoutes().then(r =>
      setRoutes((r.data || []).filter(r => !r.route_number?.startsWith("TEST")))
    );
  }, []);

  useEffect(() => {
    if (activeTab === "summary" && date) fetchSummary();
    if (activeTab === "food" && date) fetchFoodReq();
    if (activeTab === "entry" && entryMode === "route" && selectedRoute && date) fetchExisting();
  }, [activeTab, date, selectedRoute, selectedKitchen, entryMode]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const r = await schoolIndentAPI.getSummary({ date, kitchen_id: selectedKitchen || null });
      setSummary(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); } setLoading(false);
  };

  const fetchFoodReq = async () => {
    setLoading(true);
    try {
      const r = await schoolIndentAPI.getFoodRequirement({ date, kitchen_id: selectedKitchen || null });
      setFoodReq(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); } setLoading(false);
  };

  const fetchExisting = async () => {
    try {
      const r = await schoolIndentAPI.getEntries({ date, route_id: selectedRoute, limit: 2000 });
      const entries = r.data || [];
      const data = {};
      entries.forEach(e => {
        const sn = e.school_name;
        if (!data[sn]) data[sn] = { MDM: emptyStrength(), breakfast: emptyStrength() };
        const prog = e.program_type === "breakfast" ? "breakfast" : "MDM";
        data[sn][prog] = e.strength || emptyStrength();
      });
      setBulkData(data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const currentRoute = routes.find(r => r.id === selectedRoute);
  const schools = currentRoute?.schools || [];
  const filteredSchools = schools.filter(s =>
    !schoolSearch || s.name?.toLowerCase().includes(schoolSearch.toLowerCase())
  );
  const updateCount = (schoolName, program, cls, val) => {
    setBulkData(prev => {
      const school = prev[schoolName] || { MDM: emptyStrength(), breakfast: emptyStrength() };
      return { ...prev, [schoolName]: { ...school, [program]: { ...school[program], [cls]: parseInt(val) || 0 } } };
    });
  };
  const routeTotals = filteredSchools.reduce((acc, s) => {
    const t = { MDM: totalOf(bulkData[s.name]?.MDM), breakfast: totalOf(bulkData[s.name]?.breakfast) };
    acc.MDM += t.MDM; acc.breakfast += t.breakfast;
    return acc;
  }, { MDM: 0, breakfast: 0 });

  const saveRouteAll = async () => {
    if (!selectedRoute || !date) { setError("Select a route and date"); return; }
    setSaving(true); setError("");
    const entries = [];
    Object.entries(bulkData).forEach(([schoolName, programs]) => {
      ["MDM", "breakfast"].forEach(prog => {
        const total = totalOf(programs[prog]);
        if (total > 0) entries.push({ school_name: schoolName, route_id: selectedRoute, route_number: currentRoute?.route_number || "", date, kitchen_id: selectedKitchen || null, strength: programs[prog], program_type: prog });
      });
    });
    if (entries.length === 0) { setError("No strength entered"); setSaving(false); return; }
    try {
      const r = await schoolIndentAPI.bulkEntry({ date, kitchen_id: selectedKitchen || null, entries });
      setSuccess(`Saved ${r.data.saved} entries!`);
      setTimeout(() => setSuccess(""), 4000);
      setRefreshKey(k => k + 1);
    } catch (e) { setError(formatError(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="school-indent">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">School Indent Entry</h1>
        <p className="text-sm text-[#8A7E73] mt-0.5">
          Enter daily school strength for <span className="font-bold text-amber-600">Breakfast</span> &amp; <span className="font-bold text-green-600">Mid-Day Meal</span> separately
        </p>
      </div>

      {success && (
        <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64] flex items-center gap-2">
          <Check size={15} /> {success}
        </div>
      )}

      {/* Main tabs */}
      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ── STRENGTH ENTRY TAB ── */}
      {activeTab === "entry" && (
        <div className="space-y-5">
          {/* Date + Mode switcher */}
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input
                ref={dateRef}
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                onKeyDown={e => e.key === "Tab" && e.stopPropagation()}
                className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] font-medium"
                data-testid="date-input"
              />
            </div>
            {/* Mode toggle */}
            <div className="flex gap-1 bg-[#F4F3F0] rounded-xl p-1">
              <button
                onClick={() => setEntryMode("quick")}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${entryMode === "quick" ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73]"}`}
              >
                Quick Entry (Keyboard)
              </button>
              <button
                onClick={() => setEntryMode("route")}
                className={`px-4 py-2 rounded-lg text-xs font-bold transition-colors ${entryMode === "route" ? "bg-white text-[#3A322C] shadow-sm" : "text-[#8A7E73]"}`}
              >
                Route-wise Entry
              </button>
            </div>
          </div>

          {/* ── QUICK ENTRY MODE ── */}
          {entryMode === "quick" && (
            <>
              <QuickEntryRow
                date={date}
                kitchenId={selectedKitchen}
                onSaved={() => setRefreshKey(k => k + 1)}
                todayEntries={[]}
              />
              <TodaysEntriesLog date={date} kitchenId={selectedKitchen} refreshKey={refreshKey} />
            </>
          )}

          {/* ── ROUTE-WISE MODE ── */}
          {entryMode === "route" && (
            <div className="space-y-4">
              <div className="flex gap-3 flex-wrap items-end">
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route *</label>
                  <select value={selectedRoute} onChange={e => setSelectedRoute(e.target.value)}
                    className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
                    <option value="">Select Route</option>
                    {routes.map(r => (
                      <option key={r.id} value={r.id}>{r.route_number} — {r.coordinator_name || r.name} ({r.schools?.length || 0} schools)</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                  <input placeholder="Filter schools in route…" value={schoolSearch} onChange={e => setSchoolSearch(e.target.value)}
                    className="border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D] w-56" />
                </div>
                {(routeTotals.MDM > 0 || routeTotals.breakfast > 0) && (
                  <div className="flex gap-2 ml-auto">
                    <span className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-sm font-bold text-green-700">MDM: {routeTotals.MDM.toLocaleString()}</span>
                    <span className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-sm font-bold text-amber-700">BF: {routeTotals.breakfast.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {!selectedRoute ? (
                <div className="text-center py-16 text-[#8A7E73] bg-white border border-[#E5E1DB] rounded-2xl">
                  <Users size={48} className="mx-auto mb-3 opacity-20" />
                  <p>Select a route to view schools</p>
                </div>
              ) : (
                <>
                  <div className="text-xs text-[#8A7E73]">{filteredSchools.length} schools in route</div>
                  <div className="space-y-2">
                    {filteredSchools.map(school => {
                      const mdmTotal = totalOf(bulkData[school.name]?.MDM);
                      const bfTotal  = totalOf(bulkData[school.name]?.breakfast);
                      const hasData  = mdmTotal + bfTotal > 0;
                      const isExpanded = expandedSchool === school.name;
                      return (
                        <div key={school.name} className={`bg-white border rounded-xl overflow-hidden ${hasData ? "border-[#637C64]/30" : "border-[#E5E1DB]"}`}>
                          <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAF8]"
                            onClick={() => setExpandedSchool(isExpanded ? null : school.name)}>
                            {isExpanded ? <ChevronDown size={16} className="text-[#8A7E73]" /> : <ChevronRight size={16} className="text-[#8A7E73]" />}
                            <div className="flex-1">
                              <p className="font-medium text-[#3A322C] text-sm">{school.name}</p>
                              <p className="text-xs text-[#8A7E73]">{school.address}</p>
                            </div>
                            {hasData ? (
                              <div className="flex items-center gap-2">
                                {mdmTotal > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">MDM: {mdmTotal}</span>}
                                {bfTotal > 0  && <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">BF: {bfTotal}</span>}
                                <Check size={14} className="text-[#637C64]" />
                              </div>
                            ) : <span className="text-xs text-[#C4BAAF]">No entry</span>}
                          </div>
                          {isExpanded && (
                            <div className="border-t border-[#F4F3F0]">
                              {[["MDM", <Sun size={13} className="text-green-700" />, "border-green-300 bg-green-50", "text-green-700", "MDM (Lunch)"],
                                ["breakfast", <Coffee size={13} className="text-amber-700" />, "border-amber-300 bg-amber-50", "text-amber-700", "Breakfast"]].map(([prog, icon, color, text, label]) => {
                                  const strength = bulkData[school.name]?.[prog] || emptyStrength();
                                  const total = totalOf(strength);
                                  return (
                                    <div key={prog} className={`px-4 py-3 border-b border-[#F4F3F0] last:border-b-0 ${color} bg-opacity-30`}>
                                      <div className="flex items-center gap-2 mb-2">
                                        {icon}
                                        <span className={`text-xs font-bold uppercase tracking-wide ${text}`}>{label}</span>
                                        {total > 0 && <span className={`text-xs font-bold ml-auto ${text}`}>{total.toLocaleString()} students</span>}
                                      </div>
                                      <div className="grid grid-cols-7 gap-1.5">
                                        {CLASS_LIST.map((cls, i) => (
                                          <div key={cls} className="text-center">
                                            <label className="text-[9px] font-bold text-[#8A7E73] block mb-1">{CLASS_SHORT[i]}</label>
                                            <input
                                              type="number" min="0" max="9999"
                                              value={strength[cls] || ""}
                                              onChange={e => updateCount(school.name, prog, cls, e.target.value)}
                                              placeholder="0"
                                              className="w-full border border-[#E5E1DB] rounded px-1 py-1 text-xs text-center outline-none focus:border-[#E8956D] bg-white"
                                            />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {error && <div className="bg-[#FBEAE7] rounded-lg px-4 py-2.5 text-sm text-[#C2624E]">{error}</div>}
                  <button onClick={saveRouteAll} disabled={saving}
                    className="w-full bg-[#E8956D] text-[#3A322C] rounded-xl py-3 font-bold flex items-center justify-center gap-2 hover:bg-[#D48661] disabled:opacity-60">
                    {saving ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
                    Save Route {currentRoute?.route_number} — MDM: {routeTotals.MDM.toLocaleString()} · Breakfast: {routeTotals.breakfast.toLocaleString()}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── DAILY SUMMARY TAB ── */}
      {activeTab === "summary" && (
        <div className="space-y-4">
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            <button onClick={fetchSummary} className="border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">Refresh</button>
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : summary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "MDM Students",       value: (summary.mdm_students || summary.total_students)?.toLocaleString(), color: "text-green-700" },
                  { label: "Breakfast Students",  value: summary.breakfast_students?.toLocaleString(), color: "text-amber-600" },
                  { label: "Schools Entered",     value: summary.school_count, color: "text-[#637C64]" },
                  { label: "Egg Supply",          value: summary.egg_students > 0 ? summary.egg_students?.toLocaleString() : "—", color: "text-purple-700" },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-center">
                    <p className={`text-2xl font-extrabold font-[Manrope] ${s.color}`}>{s.value ?? "—"}</p>
                    <p className="text-xs text-[#8A7E73] mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {Object.keys(summary.by_route || {}).length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <h3 className="font-bold text-[#3A322C] mb-3 text-sm uppercase tracking-wide">By Route</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(summary.by_route).map(([route, count]) => (
                      <div key={route} className="bg-[#F4F3F0] rounded-lg p-3 text-center">
                        <p className="font-bold text-[#E8956D] text-lg">{count?.toLocaleString()}</p>
                        <p className="text-xs text-[#5C5147] font-medium">{route} Route</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                  <h3 className="font-bold text-[#3A322C] text-sm">Class-wise Strength</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Class","Students"].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#5C5147]">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {CLASS_LIST.map(cls => {
                        const count = summary.class_breakdown?.[cls] || 0;
                        return count > 0 ? (
                          <tr key={cls} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-2.5 font-medium text-[#3A322C]">{cls}</td>
                            <td className="px-4 py-2.5 font-bold text-[#E8956D]">{count.toLocaleString()}</td>
                          </tr>
                        ) : null;
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {!loading && !summary && <div className="text-center py-12 text-[#8A7E73]">No data for this date</div>}
        </div>
      )}

      {/* ── FOOD REQUIREMENT TAB ── */}
      {activeTab === "food" && (
        <div className="space-y-4">
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            <button onClick={fetchFoodReq} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold">Calculate</button>
          </div>
          {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : foodReq && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "MDM Students",        value: foodReq.total_mdm_students?.toLocaleString(),       color: "text-green-700" },
                  { label: "Breakfast Students",   value: foodReq.total_breakfast_students?.toLocaleString(), color: "text-amber-700" },
                  { label: "Schools",              value: foodReq.school_count,                               color: "text-[#E8956D]" },
                ].map(s => (
                  <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-center">
                    <p className={`text-2xl font-extrabold font-[Manrope] ${s.color}`}>{s.value || 0}</p>
                    <p className="text-xs text-[#8A7E73] mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {foodReq.ingredients?.length > 0 ? (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-[#3A322C] text-sm">Ingredients Required</h3>
                    <button onClick={() => exportCSV(foodReq.ingredients, `food_req_${date}`)} className="text-xs text-[#E8956D] font-bold">Export CSV</button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Ingredient","Quantity","Unit"].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {foodReq.ingredients.map((ing, i) => (
                        <tr key={name} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{ing.item_name}</td>
                          <td className="px-4 py-3 font-bold text-[#E8956D]">{ing.total_quantity}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{ing.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-5 py-4 text-sm text-[#D98032]">
                  {foodReq.message || "No ingredients calculated — ensure school strength and menu plan are entered."}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
